import { useEffect } from "react";
import { useAppStore } from "../../../providers/AppStore";
import { navigateFromAgentSelector } from "../../consultation/consultationNavBridge";

const CHEATSHEET_AGENT_ID = "agent_01jz2n4p76eg9vrrgd0xe2z0v9";
const DASHBOARD_AGENT_ID = "agent_6801kbmzn6eyekhvmtf8mgb5q13s";
const CONSULTATION_AGENT_ID = "agent_0201kpzmr3v2f6cv05xrp1sayq1p";
const WIDGET_SCRIPT_URL = "https://unpkg.com/@elevenlabs/convai-widget-embed";

export const useElevenLabsWidget = (
  reportId?: string,
  agentType?: string,
  selectedClientId?: string | null,
  isDataLoaded?: boolean,
  enabled: boolean = true
) => {
  const { ASSelectedClient } = useAppStore();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Skip initialization if no agent type is provided
    if (!agentType) {
      return;
    }

    // For agents that read live customer data via the secret__token
    // dynamic var, wait for the data fetch to resolve so the widget never
    // talks to the API with a stale or empty selected client.
    if (agentType === "dashboard" || agentType === "consultation") {
      if (!selectedClientId || isDataLoaded === false) {
        return;
      }
    }
    let cleanupFn: (() => void) | undefined;

    const initializeWidget = () => {
      try {
        // Check if WebGL is available (required by ElevenLabs widget)
        const testCanvas = document.createElement("canvas");
        const gl =
          testCanvas.getContext("webgl") ||
          testCanvas.getContext("experimental-webgl");
        if (!gl) {
          console.error(
            "WebGL is not available. The ElevenLabs widget requires WebGL support.\n" +
              "Please enable hardware acceleration in chrome://settings/system"
          );
          return;
        }

        const container = document.getElementById("chat-container");
        if (!container) {
          console.error("Chat container element not found");
          return;
        }

        // Check if widget already exists
        if (container.querySelector("elevenlabs-convai")) {
          return;
        }

        // Setup client tools for widget interaction
        let lastFocusedEl: HTMLElement | null = null;

        const handleCallEvent = (event: Event) => {
          const customEvent = event as CustomEvent;

          // Expose client tools for the agent to interact with the page
          customEvent.detail.config.clientTools = {
            focusDom: ({ selector }: { selector: string }) => {
              try {
                // Security: only allow data-agent-target selectors
                if (!selector.startsWith("[data-agent-target=")) {
                  return { ok: false, error: "selector_not_allowed" };
                }

                // Consultation stepper navigation, overloaded onto focusDom:
                // a virtual target `consultation-nav:<section>/<sub>` (no DOM
                // element carries it) navigates the stepper via its URL
                // params — client-side, so the widget + live session survive.
                // `null` means "not a nav target" → normal DOM focus below.
                // Unknown/hidden targets return `{ ok:false }` and DON'T
                // navigate. See consultationNavBridge.ts.
                const nav = navigateFromAgentSelector(selector);
                if (nav) return nav;

                const el = document.querySelector(selector) as HTMLElement;
                if (!el) return { ok: false, error: "not_found" };

                // Remove highlight from previous element
                if (lastFocusedEl && lastFocusedEl !== el) {
                  lastFocusedEl.classList.remove("agent-highlight");
                  if (typeof lastFocusedEl.blur === "function") {
                    lastFocusedEl.blur();
                  }
                }

                // Scroll, focus, and highlight the new element
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                if (typeof el.focus === "function") {
                  el.focus({ preventScroll: true });
                }
                el.classList.add("agent-highlight");

                lastFocusedEl = el;
                return { ok: true };
              } catch (e) {
                return { ok: false, error: String(e) };
              }
            },
            clickDom: ({ selector }: { selector: string }) => {
              try {
                // Security: only allow data-agent-target selectors
                if (!selector.startsWith("[data-agent-target=")) {
                  return { ok: false, error: "selector_not_allowed" };
                }

                const el = document.querySelector(selector) as HTMLElement;
                if (!el) return { ok: false, error: "not_found" };

                // Scroll the element into view before clicking it
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                if (typeof el.click === "function") {
                  el.click();
                } else {
                  return { ok: false, error: "not_clickable" };
                }

                return { ok: true };
              } catch (e) {
                return { ok: false, error: String(e) };
              }
            },
          };
        };

        const createWidget = () => {
          // Create the widget element
          const widget = document.createElement("elevenlabs-convai");
          widget.setAttribute(
            "agent-id",
            agentType === "Cheatsheet"
              ? CHEATSHEET_AGENT_ID
              : agentType === "consultation"
              ? CONSULTATION_AGENT_ID
              : DASHBOARD_AGENT_ID
          );
          widget.setAttribute("variant", "expanded");

          // Set dynamic variables based on agent type
          const dynamicVars: Record<string, any> = {};

          // Environment suffix — passed to every agent so its API-host
          // construction matches the frontend's environment. `""` for prod
          // (welcome.mypnoe.com → pnoeapi.mypnoe.com); `"-dev"` for dev +
          // localhost (welcome-dev.mypnoe.com → pnoeapi-dev.mypnoe.com).
          // Mirrors `getEnvFromDomain` in `providers/AppStore.tsx`.
          const hostname = window.location.hostname;
          const envSuffix =
            hostname === "welcome.mypnoe.com" ||
            hostname === "welcome-staging.mypnoe.com"
              ? ""
              : "-dev";
          dynamicVars.env_suffix = envSuffix;

          if (agentType === "dashboard" || agentType === "consultation") {
            // Pass only minimal essential data - large objects cause "Invalid message" errors
            dynamicVars.secret__CustomerID = selectedClientId;
            dynamicVars.secret__token = ASSelectedClient
              ? `Bearer ${ASSelectedClient.token}`
              : "";
            dynamicVars.secret__refresh_token = ASSelectedClient
              ? ASSelectedClient.refresh_token
              : "";
          }

          if (reportId !== "" && agentType === "Cheatsheet") {
            dynamicVars.report_id = reportId;
          }

          // Apply dynamic variables if any were set
          if (Object.keys(dynamicVars).length > 0) {
            widget.setAttribute(
              "dynamic-variables",
              JSON.stringify(dynamicVars)
            );
          }

          container.appendChild(widget);

          // Shift the widget's floating layers up 50px so they clear the
          // consultation stepper bar. Inject immediately so the controls do
          // not visibly jump.
          //
          // What each rule targets:
          //   • `.rounded-compact-sheet` is the collapsed cluster (and the
          //     single collapse button while the chat is open).
          //   • `.sheet` is the expanded chat panel. It is `position:
          //     absolute; bottom: 80px` inside its overlay — 80px = the
          //     cluster's 60px + a 20px gap — so without the same shift the
          //     raised cluster pokes out from under its bottom edge and the
          //     panel itself sits on the stepper. `margin-bottom` moves an
          //     absolutely positioned box up just like the cluster.
          //   • `.overlay > p` matches the attribution paragraph, which is
          //     a direct child of its own sibling `.overlay` layer.
          //   • `.overlay:has(.terms)` is the Terms & Conditions sheet's
          //     layer. The attribution's `.overlay` is a LATER sibling with
          //     `z-index: auto`, so once we shift the attribution up 50px it
          //     paints over the bottom edge of the terms sheet ("Powered by"
          //     peeks out between Cancel / Accept). Lifting the terms layer
          //     above it (both are `position: absolute`) puts the sheet back
          //     on top; nothing else lives in that layer.
          //
          // Retry pattern: the widget's `shadowRoot` is attached during the
          // custom-element `connectedCallback` (synchronously on most web
          // components, including this one), so the first attempt usually
          // succeeds. The rAF fallback covers cases where the script hasn't
          // finished defining the element on this paint.
          if (agentType === "consultation") {
            const injectShadowStyles = (host: HTMLElement, attempts = 0) => {
              const shadow = (
                host as HTMLElement & {
                  shadowRoot: ShadowRoot | null;
                }
              ).shadowRoot;
              if (!shadow) {
                if (attempts < 20) {
                  requestAnimationFrame(() =>
                    injectShadowStyles(host, attempts + 1)
                  );
                }
                return;
              }
              if (shadow.getElementById("pnoe-convai-compact-shift")) return;
              const styleEl = document.createElement("style");
              styleEl.id = "pnoe-convai-compact-shift";
              styleEl.textContent = `
                .rounded-compact-sheet,
                .sheet {
                  margin-bottom: 50px !important;
                }
                .overlay > p {
                  margin-bottom: 50px !important;
                }
                .overlay:has(.terms) {
                  z-index: 20 !important;
                }
              `;
              shadow.appendChild(styleEl);
            };
            injectShadowStyles(widget);
          }

          // Setup event listener after widget is ready
          setTimeout(() => {
            const widgetEl = document.querySelector("elevenlabs-convai");
            if (widgetEl) {
              widgetEl.addEventListener(
                "elevenlabs-convai:call",
                handleCallEvent
              );
            }
          }, 500);
        };

        // Check if script is already loaded
        const existingScript = document.querySelector(
          `script[src="${WIDGET_SCRIPT_URL}"]`
        );

        if (existingScript) {
          // Script already loaded, create widget directly
          requestAnimationFrame(createWidget);
        } else {
          // Load the ElevenLabs embed script
          const script = document.createElement("script");
          script.src = WIDGET_SCRIPT_URL;
          script.async = true;

          // Create widget after script loads
          script.onload = () => {
            // Use requestAnimationFrame to ensure the script is fully initialized
            requestAnimationFrame(createWidget);
          };

          script.onerror = () => {
            console.error("Failed to load ElevenLabs widget script");
          };

          document.body.appendChild(script);
        }

        // Cleanup function - only remove widget, not script
        // The script defines a custom element which can only be registered once
        cleanupFn = () => {
          const widgetEl = document.querySelector("elevenlabs-convai");
          if (widgetEl) {
            widgetEl.removeEventListener(
              "elevenlabs-convai:call",
              handleCallEvent
            );
            widgetEl.remove();
          }
          // Don't remove script - custom elements can only be defined once
        };
      } catch (err: any) {
        console.error("Error initializing ElevenLabs widget:", err.message);
      }
    };

    // Ensure DOM is ready before initializing
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initializeWidget);
      return () => {
        document.removeEventListener("DOMContentLoaded", initializeWidget);
        cleanupFn?.();
      };
    } else {
      // DOM is already ready, initialize after React finishes rendering
      const timer = setTimeout(initializeWidget, 100);
      return () => {
        clearTimeout(timer);
        cleanupFn?.();
      };
    }
  }, [
    reportId,
    agentType,
    selectedClientId,
    isDataLoaded,
    enabled,
  ]);
};
